import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { branding, getOrgShortName } from "../../config";

export function NavBarPublic() {
  const [navItems, setNavItems] = useState([]);
  const [logo, setLogo] = useState({ text: getOrgShortName(), image: "", useImage: false });
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're on the homepage
  const isHomePage = location.pathname === "/" || location.pathname === "/home";
  
  // Handle scroll for transparent navbar
  useEffect(() => {
    if (!isHomePage) return;
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  useEffect(() => {
    axios.get("/api/navbar")
      .then(res => setNavItems(res.data))
      .catch(() => setNavItems([]));
  }, []);

  useEffect(() => {
    axios.get("/api/public/logo")
      .then(res => {
        const logoData = res.data.content;
        if (logoData) {
          setLogo(logoData);
        }
      })
      .catch(() => {
        // Use default from config if fetch fails
        setLogo({ text: getOrgShortName(), image: "", useImage: false });
      });
  }, []);

  /* toggle children on mobile */
  const [openIdx, setOpenIdx] = useState(null);

  const handleCaretClick = (idx) => {
    setOpenIdx(openIdx === idx ? null : idx);
  };

  return (
    <>
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap"
      />
      
      {/* Dynamic styles for navbar */}
      <style>
        {`
          .navbar-custom .nav-link {
            color: rgba(255, 255, 255, 0.9) !important;
          }
          .navbar-custom .nav-link:hover {
            color: white !important;
          }
          .navbar-custom .dropdown-item {
            color: #333 !important;
          }
          
          /* White text for dropdown items on mobile */
          @media (max-width: 991.98px) {
            .navbar-custom .dropdown-item {
              color: rgba(255, 255, 255, 0.9) !important;
              background-color: transparent !important;
            }
            .navbar-custom .dropdown-item:hover {
              color: white !important;
              background-color: rgba(255, 255, 255, 0.1) !important;
            }
          }
          .navbar-custom .btn-custom-outline {
            border-color: white !important;
            color: white !important;
          }
          .navbar-custom .btn-custom-outline:hover {
            background-color: white !important;
            color: #025387 !important;
          }
          
          /* Mobile menu background */
          @media (max-width: 991.98px) {
            .navbar-custom .navbar-collapse.show,
            .navbar-custom .navbar-collapse.collapsing {
              background-color: #025387;
              padding: 1rem;
              margin-top: 0.5rem;
            }
            
            /* Force solid background only when menu is expanded or expanding */
            .navbar-custom:has(.navbar-collapse.show),
            .navbar-custom:has(.navbar-collapse.collapsing) {
              background-color: #025387 !important;
              transition: none !important;
            }
          }
        `}
      </style>

      <nav className="navbar navbar-expand-lg navbar-custom shadow sticky-top"
        style={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: "1.1rem", // 1.1rem originally
          // textTransform: "uppercase",
          backgroundColor: isHomePage ? (scrolled ? '#025387' : 'rgba(0, 0, 0, 0.1)') : '#025387',
          transition: 'background-color 0.3s ease',
        }}>
        <div className="container py-2">
          <Link className="navbar-brand"
            to="/" style={{
              display: 'flex',
              alignItems: 'center',
              margin: 0,
              padding: 0,
            }}>
            {logo.useImage && logo.image ? (
              <img 
                src={logo.image} 
                alt={logo.text}
                style={{
                  height: '60px',
                  width: 'auto',
                  marginTop: '-7px',
                  marginBottom: '-5px',
                }}
              />
            ) : (
              <span style={{
                fontSize: "1.5rem",
                letterSpacing: "1px",
                color: 'white',
                fontWeight: 'bold',
                transition: 'color 0.3s ease',
              }}>
                {logo.text}
              </span>
            )}
          </Link>

          <button className="navbar-toggler" type="button"
            data-bs-toggle="collapse" data-bs-target="#navbarNav"
            style={{
              borderColor: 'rgba(255, 255, 255, 0.8)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }}>
            <span className="navbar-toggler-icon" style={{
              filter: 'brightness(0) invert(1)'
            }} />
          </button>

          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav mb-2 mb-lg-0 ms-auto gap-4 row-gap-0">

              {/* Static home link */}
              <li className="nav-item"><Link className="nav-link" to="/">Home</Link></li>

              {/* Dynamically rendered items */}
              {navItems.map((item, idx) =>
                item.children?.length ? (
                  <li className="nav-item dropdown" key={idx}>
                    <Link className="nav-link" to={`/${item.slug || ""}`}>
                      {item.label}
                    </Link>

                    <ul className="dropdown-menu show">
                      {item.children.map((c, j) => (
                        <li key={j}>
                          <Link className="dropdown-item fw-normal" to={`/${c.slug}`}>
                            {c.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  <li className="nav-item" key={idx}>
                    <Link className="nav-link" to={`/${item.slug}`}>{item.label}</Link>
                  </li>
                )
              )}

            </ul>

            <div className="d-flex ms-auto gap-2">
              <Link to="/rent"
                className="btn btn-custom-outline px-3 py-2 fw-bold"
                style={{ whiteSpace: 'nowrap' }}>
                Pay Rent
              </Link>
              <Link to="/donate"
                className="btn btn-custom-filled px-3 py-2 fw-bold"
                style={{ whiteSpace: 'nowrap' }}>
                Donate
              </Link>
            </div>
            
          </div>
        </div>
      </nav>
    </>
  );
}
